import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';

import { Alert } from '@@/Alert';
import { Button } from '@@/buttons';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import type { Option as PortainerOption } from '@@/form-components/PortainerSelect';
import {
  EditableDatatable,
  NEW_ROW_ID,
} from '@@/datatables/editable/EditableDatatable';
import { isIdForNewRow } from '@@/datatables/editable/actionsColumn';

import { DockerRegistryFormValues } from '../types';

import type { AccessRow } from './columns/helper';
import { useColumns } from './columns/useColumns';

const accessStorageKey = 'docker-registry-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface Props {
  newIdCounter: number;
  setNewIdCounter: (newIdCounter: number) => void;
}

export function DockerRegistryAccessDatatable({
  newIdCounter,
  setNewIdCounter,
}: Props) {
  const {
    values: { accessRows = [] },
    setFieldValue,
  } = useFormikContext<DockerRegistryFormValues>();
  const { rowsWithNames, userTeamOptions, users, teams, isLoading, error } =
    useDockerRegistryAccessRows(accessRows);

  const tableState = useTableState(accessSettingsStore, accessStorageKey);
  const [newEditableIdCounter, setNewEditableIdCounter] = useState(NEW_ROW_ID);

  const columns = useColumns({
    accessRows,
    onChange,
    userTeamOptions,
    users,
    teams,
    handleAccessChange,
    onRemove: (item) => handleRemoveRows([item]),
  });

  if (error) {
    return (
      <Alert color="error" title="Error loading data">
        {(error as Error)?.message ??
          'Unable to load registries, users, or teams'}
      </Alert>
    );
  }

  return (
    <EditableDatatable
      title="Registry access list"
      dataset={rowsWithNames}
      columns={columns}
      revertRow={revertRow}
      acceptRow={acceptRow}
      settingsManager={tableState}
      isLoading={isLoading}
      renderTableActions={(selectedRows) => (
        <>
          <Button
            icon={Plus}
            color="primary"
            size="small"
            onClick={addAccess}
            /* currently limit to 1 new row */
            disabled={accessRows.some((row) => isIdForNewRow(row.id))}
            data-cy="docker-registry-access-datatable-add-access-button"
            className="!ml-0"
          >
            Add Access
          </Button>
          <Button
            color="dangerlight"
            size="small"
            icon={Trash2}
            onClick={() => handleRemoveRows(selectedRows)}
            data-cy="docker-registry-access-datatable-remove-access-button"
            disabled={selectedRows.length === 0}
          >
            Remove
          </Button>
        </>
      )}
      data-cy="docker-registry-access-datatable"
      emptyContentLabel="No registry access configured"
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function onChange(val: AccessRow[]) {
    return setFieldValue('accessRows', val);
  }

  function handleRemoveRows(items: AccessRow[]) {
    const idsToRemove = new Set(items.map((item) => item.registryId));
    const newAccessRows = accessRows.filter(
      (row) => !idsToRemove.has(row.registryId)
    );
    onChange(newAccessRows);
  }

  function handleAccessChange(
    item: AccessRow,
    selection: readonly AccessOption[]
  ) {
    const newAccessRows = [...accessRows];
    const index = newAccessRows.findIndex(
      (row) => row.registryId === item.registryId
    );
    if (index !== -1) {
      const userIds = selection
        .filter((o) => o.Type === 'user')
        .map((o) => o.Id);
      const teamIds = selection
        .filter((o) => o.Type === 'team')
        .map((o) => o.Id);
      newAccessRows[index] = { ...newAccessRows[index], userIds, teamIds };
      onChange(newAccessRows);
    }
  }

  function revertRow(rowIndex: number) {
    onChange([
      ...accessRows.slice(0, rowIndex),
      ...accessRows.slice(rowIndex + 1),
    ]);
  }

  function acceptRow(rowIndex: number) {
    const existingRowIndex = accessRows.findIndex(
      (a, index) =>
        index !== rowIndex &&
        isIdForNewRow(a.id) &&
        a.registryId === accessRows[rowIndex].registryId
    );
    const newRow = cloneDeep(accessRows[rowIndex]);

    if (existingRowIndex === -1) {
      newRow.id = newIdCounter;
      setNewIdCounter(newIdCounter + 1);
      const newAccessRows = [
        newRow,
        ...accessRows.slice(0, rowIndex),
        ...accessRows.slice(rowIndex + 1),
      ];
      onChange(newAccessRows);
    } else {
      const newAccessRows = [
        ...accessRows.slice(0, rowIndex),
        ...accessRows.slice(rowIndex + 1),
      ];
      newAccessRows[existingRowIndex - 1].teamIds = newRow.teamIds;
      newAccessRows[existingRowIndex - 1].userIds = newRow.userIds;
      onChange(newAccessRows);
    }
  }

  function addAccess() {
    const newAccessRow: AccessRow = {
      id: newEditableIdCounter,
      registryId: null,
      userIds: [],
      teamIds: [],
      registryName: '',
    };

    const newAccessRows = [cloneDeep(newAccessRow), ...accessRows];
    onChange(newAccessRows);
    setNewEditableIdCounter((count) => count - 1);
  }
}

function useDockerRegistryAccessRows(
  accessRows: DockerRegistryFormValues['accessRows']
) {
  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });

  const usersQuery = useUsers(false, 0, true, (users) =>
    users.map((u) => ({
      Type: 'user' as const,
      Id: u.Id,
      Name: u.Username,
    }))
  );

  const teamsQuery = useTeams(false, 0, {
    select: (teams) =>
      teams.map((t) => ({
        Type: 'team' as const,
        Id: t.Id,
        Name: t.Name,
      })),
  });

  const users = usersQuery.data || [];
  const teams = teamsQuery.data || [];

  const userTeamOptions: PortainerOption<AccessOption>[] = useMemo(() => {
    const userOptions = usersQuery.data || [];
    const teamOptions = teamsQuery.data || [];
    return [...userOptions, ...teamOptions].map((v) => ({
      value: v,
      label: v.Name,
    }));
  }, [usersQuery.data, teamsQuery.data]);

  const rowsWithNames = useMemo<AccessRow[]>(() => {
    const registries = registriesQuery.data || [];
    return accessRows.map((row) => {
      const registry = registries.find((r) => r.Id === row.registryId);
      return {
        ...row,
        registryName: registry?.Name || 'Unknown',
      };
    });
  }, [accessRows, registriesQuery.data]);

  const isLoading =
    registriesQuery.isLoading || usersQuery.isLoading || teamsQuery.isLoading;

  const error = registriesQuery.error || usersQuery.error || teamsQuery.error;

  return {
    rowsWithNames,
    userTeamOptions,
    users,
    teams,
    isLoading,
    error,
  };
}
